import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '@polymer/iron-ajax/iron-ajax.js';
import {Translations} from './translations.js';
import template from './ns-scan-cards.html';
import {TranslationsUtil, ConfigService} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import {ProductService, ScanCardService} from '@nuskin/ns-shop';

/**
 * Image Imports
 */
import closeIcon from '../img/ic-close-small.svg';
import infoIcon  from '../img/info-icon-blue.svg';

/**
 * An element to showcase the scan cards in an ADR order.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>This component listens to a scanCardsDataObject property and populates it with
 *  necessary scan card information.</p>
 * </section>
 *
 * <h1>Scan Cards Component Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-scan-cards limit="2" is-demo></ns-scan-cards>
 *   </template>
 * </demo-snippet>
 */
export class NsScanCardsRepeat extends PolymerElement {
  static get is () {
    return 'ns-scan-cards-repeat';
  }

  /**
   * Sets the html template
   */
  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  /**
   * Sets properties to pass to this component
   */
  static get properties () {
    return {
      /**
       * Country property
       *
       * Examples:
       *  - 'US', 'DE'
       */
      country: {
        type: String
      },
      /**
       * Language property.
       *
       * Examples:
       *  - 'en', 'es'
       */
      language: {
        type: String
      },
      /**
       * Scan card count limit, in positive integers. Defaults to 0.
       *
       * Examples:
       *  - 2, 3
       */
      limit: {
        type: Number,
        value: 10
      },
      /**
       * ADR number
       */
      adrnumber: {
        type: String
      },
      /**
       * URL for loading translations data
       */
      loadDataUrl: {
        type: String
      },
      /**
       * Translations data
       */
      translations: {
        type: JSON
      },
      /**
       * Scan cards data object
       */
      data: {
        type: Object,
        value: []
      },
      /**
       * Flag indicating whether the there is error in save
       */
      hasError: {
        type: Boolean,
        value: false
      },
      /**
       * Error message to be displayed when saving fails
       */
      errorMessage: {
        type: String
      },
      /**
       * Count for line items in the ADR
       */
      lineItemCount: {
        type: Number,
        value: 10
      },
      /**
       * Count for product details responses received to track scanQualified number
       */
      scanQualifiedResponsesReceived: {
        type: Number,
        value: 10,
        observer: '_scanQualifiedResponsesReceived'
      },
      /**
       * Object used to share data between ns-scan-cards and ns-adp-wrapper.
       */
      scanCardsDataObject: {
        type: Object,
        notify: true
      },
      /**
       * Encoded value of close icon used for passing this value to the template
       */
      closeIcon: {
        type: String,
        value: closeIcon
      },
      /**
       * Encoded value of info icon used for passing this value to the template
       */
      infoIcon: {
        type: String,
        value: infoIcon
      },
      /**
       * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Shows a toast of the indicated theme ('success', 'error', etc.)
       * Set to false to hide all toasts
       */
      toastState: {
        type: String,
        notify: true
      },
      /**
       * Product Response for scan cards
       * Demo/Test purposes
       */
      productTestResponse: {
        type: Array
      }
    };
  }

  static get observers () {
    return [
      '_scanCardsDataObjectChanged(scanCardsDataObject.updateType)'
    ];
  }

  /**
   * This is a polymer lifecycle event.
   * Loads existing scan cards.
   */
  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();
    if (this.isDemo) {
      this.productTestResponse = productResponse[0];
      this.scanCardsDataObject = scanCardsTestObject;
    }
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => this[key] = this[key] || value);
  }

  /**
   * Observer when scanCardsDataObject response is changed.
   */
  _scanCardsDataObjectChanged () {
    if (!this.isDemo) {
      this._handleLoadResponse(this.scanCardsDataObject);
    } else {
      this.limit += this.productTestResponse.market.scanQualified;
      this.scanQualifiedResponsesReceived++;
    }
  }

  /**
   * Observer when this.data is updated. Set this.hasData property.
   */
  _dataUpdated () {
    let hasCards;
    if (this.data === null || this.data.length === 0) {
      hasCards = false;
    } else {
      hasCards = this.data[0].cardNumber !== '';
    }

    if (this.shadowRoot.querySelector('#noScanCardMessageLabel') !== null) {
      this.shadowRoot.querySelector('#noScanCardMessageLabel').style.display = hasCards ? 'none' : 'block';
      this.shadowRoot.querySelector('#cardNumberLabel').style.display = hasCards ? 'block' : 'none';
      this.shadowRoot.querySelector('#cardNameLabel').style.display = hasCards ? 'block' : 'none';
      this.shadowRoot.querySelector('#scanCardDescription').style.display = hasCards ? 'none' : 'block';
    }
  }

  /**
   * Remove empty scan cards in this.data.
   */
  stripEmptyScanCardsData () {
    let dataLength = this.data.length;
    if (dataLength <= this.limit) {
      for (let i = dataLength - 1; i >= 0; i--) {
        if (this.data[i].cardNumber === '') {
          this.data.splice(i, 1);
        }
      }
    }
  }

  /**
   * Set the edit status to true when user click on add/edit button
   */
  _addButtonClicked () {
    this.inEdit = true;
    this.hasError = false;
    this.errorMessage = '';
    this.showScanCards = true;
    this.hasNoCards = false;
  }

  /**
   * Cancel user input of scan cards info
   */
  _cancelSave () {
    this.inEdit = false;
    this._handleLoadResponse(this.scanCardsDataObject);
    this.hasNoCards = !this.showScanCards;
    this.set('hasDuplicates', false);
  }

  /**
   * Save scan cards info from user input by clicking on save button.
   * Make sure cardholder's name is required, and no duplicate scan card
   * number can be used together. Makes sure all scan card numbers entered
   * are valid before making the service call. If no card number but cardholder's
   * name, simply ignore this entry.
   */
  _saveScanCards () {
    this.set('hasDuplicates', false);
    
    const form = this.shadowRoot.querySelector('#scanCardsForm');
    if (this.isDemo || !form || !form.validate()) {
      return;
    }

    // Check for duplicate scan card numbers
    for (let i = 0; i < this.data.length - 1; i++) {
      // Skip empty entries
      if (!this.data[i].ScanNumber) continue;

      for (let j = i + 1; j < this.data.length; j++) {
        if (this.data[i].ScanNumber === this.data[j].ScanNumber) {
          this.set('hasDuplicates', true);
          return;
        }
      }
    }

    // Save all changes
    this.scanCardsDataObject.scanCardsToUpdate = this.getScanCardsToUpdate(this.data);
    this.buildUpdateScanCardsBody(this.scanCardsDataObject.scanCardsToUpdate);
    if (this.scanCardsDataObject.scanCardsToUpdate.length > 0) {
      this.set('scanCardsDataObject.updateType', 'Update');
    }
  }

  /**
   * Returns a list of scan cards to be updated.
   *
   * @param {*} newCards: cards on display in edit page.
   * @param {*} existingCards: existing cards on record for the ADR.
   */
  getScanCardsToUpdate (newCards) {
    /**
     * ns-adp-wrapper needs to know if we're modifying an existing scan card or adding a new one. If
     * we're doing both, the event should be 'Modify existing scan card', since the response from the network
     * call follows the same pattern as a modify-only call.
     */

    const updatedCards = newCards.filter(newCard => {
      return newCard.ScanNumber 
        && newCard.Label 
        && (newCard.Action == 'ADD' || newCard.Action == 'CHANGE');
    })
    const changeType = 'Modify existing scan card'
    this.dispatchEvent(new CustomEvent('scan-cards-change', {detail: {changeType: changeType}}));
    if (this.hasNoCards) this.showScanCards = false;
    return updatedCards;
  }

  /**
   * Build PUT call body for updating scan cards.
   *
   * param {*} scanCardsToUpdate: scan cards to be updated
   */
  buildUpdateScanCardsBody (scanCardsToUpdate) {
    let body = this.buildScanCardsOperationBody();
    // add details of scan cards to be updated
    let scanData = [];
    let numberExists = false;
    for (let i = 0; i < scanCardsToUpdate.length; i++) {
      let cardInfo = {};
      if (scanData.some((data) => { return data.ScanNumber === scanCardsToUpdate[i].ScanNumber})) {
        return null;
      } else {
        cardInfo.ScanNumber = scanCardsToUpdate[i].ScanNumber;
        cardInfo.ADRNumber = this.adrnumber;
        cardInfo.Label = scanCardsToUpdate[i].Label;
        cardInfo.Active = 'true';
        cardInfo.Action = scanCardsToUpdate[i].Action;
        scanData.push(cardInfo);
      }
    }
    body.adr.Scan = scanData;
    return body;
  }

  /**
   * Build PUT call body for all scan cards manipulation actions.
   */
  buildScanCardsOperationBody () {
    let salesOrder = {};
    salesOrder.OrderAction = 'MANAGE';
    salesOrder.Reason = 'STANDARD';
    salesOrder.CountryCode = this.country;
    salesOrder.GiftReceipt = 'false';
    salesOrder.SoldToMemberID = UserService.getUser().id;

    let adr = {};
    adr.SalesOrder = salesOrder;
    adr.Scan = [];
    adr.ContractLength = '6';
    adr.timestamp = this.timestamp;

    let body = {};
    body.adr = adr;
    body.doc_number = this.adrnumber;
    return body;
  }

  /**
   * Process validation response and set error status and message if necessary.
   *
   * @param {*} valid: validation result returned from service call
   * @param {*} index: index of scan card in UI
   */
  _onValidationResponse (valid, index) {
    if (valid) {
      this.hasError = false;
    } else {
      this.hasError = true;
      this.errorMessage = `${translations[this.language]['invalidCardNumberErrorMessage']} `;
    }
  }

  /**
   * Show scan card info dialog
   */
  _showScanCardInfoDialog () {
    this.$.scanCardInfoDialog.open();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail : {closeOverlay: false}}));
  }

  /**
   * Close scan card info dialog
   */
  _closeScanCardInfoDialog () {
    this.$.scanCardInfoDialog.close();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail : {closeOverlay: true}}));
  }

  /**
   * Get consolidated error message string from error response
   *
   * params {*} messages: message array from error response
   */
  getErrorMessageStr (messages) {
    let messageStr = '';
    if (messages !== null && messages.length > 0) {
      for (let i = 0; i < messages.length; i++) {
        messageStr += messages[i].message + ' ';
      }
    }
    return messageStr;
  }

  /**
   * Call product service to get scan qualified item counts.
   *
   * params {*} lineItemDetails: list of line items in an array
   */
  getScanQualifiedForAllProducts (lineItemDetails) {
    // call product detail service for each sku
    lineItemDetails.forEach(item => {
      if (item.scanQualified >= 0) {
        this.limit += item.scanQualified * parseInt(item.requestedQuantity);
        this.scanQualifiedResponsesReceived++;
      } else {
        ProductService.getProductData(item.LineItem.SKU).then(response => {
          this.limit += item.LineItem.RequestedQuantity * response.scanQualified;
          this.scanQualifiedResponsesReceived++;
        });
      }
    });
  }

  /**
   * Reset values related to scan limits
   *
   * params {*} lineItemDetails: list of line items in an array
   */
  resetScanValues (lineItemDetails) {
    this.limit = 0;
    this.scanQualifiedResponsesReceived = 0;
    this.lineItemCount = lineItemDetails.length;
  }

  /**
   * Get response handler of "load scan cards" request.
   *
   * params {*} response: response data
   */
  _handleLoadResponse (scanCardsDataObject) {
    this.inEdit = false;
    let data = [];
    let scanCards = scanCardsDataObject.scanCards;
    const lineItemDetails = scanCardsDataObject.lineItemDetails;
    this.timestamp = scanCardsDataObject.timeStamp;
    this.adrnumber = scanCardsDataObject.adrnumber;
    if (scanCards !== undefined && scanCards != null) {
      scanCards.forEach((card) => {
        let scanCardRow = {};
        scanCardRow.ScanNumber = card.ScanNumber;
        scanCardRow.Label = card.Label;
        data.push(scanCardRow);
      });
    }
    this.data = data;
    this.showScanCards = this.data.length > 0;
    // reset scan related values and query for scanQualified count
    this.resetScanValues(lineItemDetails);
    if (lineItemDetails.length > 0) {
      this.getScanQualifiedForAllProducts(lineItemDetails);
    } else {
      this._scanQualifiedResponsesReceived();
    }
  }

  /**
   * Error response handler for loading scan cards for an ADR
   *
   * params {*} response: error response data
   */
  _handleLoadErrorResponse (response) {
    let messageStr = this.getErrorMessageStr(response.messages);
    this.hasError = true;
    this.errorMessage = messageStr;
  }

  /**
   * PUT response handler of "deactivate scan card" request.
   *
   * params {*} response: response data
   */
  handleDeactivateResponse (scan) {
    this.scanCardsDataObject.scanCards = scan;
    this.appendEmptyScanCardsData();
    this.inEdit = false;
  }

  /**
   * Error response handler for deactivating a scan card
   *
   * params {*} response: error response data
   */
  handleDeactivateError (err) {
    this.hasError = true;
    this.errorMessage = err.message;
  }

  /**
   * Error response handler for UPDATING a scan card
   *
   * params {*} response: error response data
   */
  handleUpdateError (err) {
    this.hasError = true;
    this.errorMessage = err.message;
  }

  /**
   * Append empty scan card data entries to the data object so that total
   * number of entries equals the "limit"
   */
  appendEmptyScanCardsData () {
    this.hasNoCards = (this.data.length == 0);
    let oldData = this.data;
    let dataLength = this.data.length;
    for (let i = 0; i < this.limit - dataLength; i++) {
      let rowData = {};
      oldData.push({});
    }
    this.data = [];
    this.data = oldData;
  }

  /**
   * Observer for scanQualifiedResponsesReceived count changes. If all
   * responses were received, append empty scan cards data to this.data object.
   */
  _scanQualifiedResponsesReceived () {
    if (this.scanQualifiedResponsesReceived === this.lineItemCount) {
      this.appendEmptyScanCardsData();
      // hide ns-scan-cards component if limit === 0
      this.style.display = this.limit === 0 && this.data.length === 0 
        ? 'none' 
        : 'block';
    }
  }
}

customElements.define(NsScanCardsRepeat.is, NsScanCardsRepeat);
